import { useCallback, useRef, useMemo } from 'react';
import { useMeetingSelector } from '../meeting/meetingProviderContextDef';
import {
  useParticipantTrackedState,
  PARTICIPANT_STATE_KEYS,
  EMPTY_PARTICIPANT_STATE
} from '../participant/participantStateContext';
import { useStableEvent } from '../meeting/meetingProviderContextDef';
import { events } from '../utils';

const NOOP = () => {};

const useAgentParticipant = (
  participantId,
  {
    onAgentMetrics = NOOP,
    onAgentStateChanged = NOOP,
    onAgentTranscriptionReceived = NOOP,
    onStreamEnabled = NOOP,
    onStreamDisabled = NOOP,
    onMediaStatusChanged = NOOP
  } = {}
) => {
  const allState = useParticipantTrackedState();

  const selectParticipant = useCallback(
    s => s.participants?.get(participantId),
    [participantId]
  );
  const agentParticipant = useMeetingSelector(selectParticipant) ?? null;
  const agentParticipantRef = useRef(agentParticipant);
  agentParticipantRef.current = agentParticipant;

  if (agentParticipant && !agentParticipant.isAgent) {
    throw new Error(
      `useAgentParticipant: "${participantId}" is not an agent participant. Use useParticipant() instead.`
    );
  }

  useStableEvent(
    `${events['participant-stream-enabled']}-${participantId}`,
    onStreamEnabled
  );
  useStableEvent(
    `${events['participant-stream-disabled']}-${participantId}`,
    onStreamDisabled
  );
  useStableEvent(
    `${events['participant-media-status-changed']}-${participantId}`,
    onMediaStatusChanged
  );

  useStableEvent(
    `${events['agent-state-changed']}-${participantId}`,
    onAgentStateChanged
  );
  useStableEvent(
    `${events['agent-transcription-received']}-${participantId}`,
    onAgentTranscriptionReceived
  );
  useStableEvent(`${events['agent-metrics']}-${participantId}`, onAgentMetrics);

  const remove = useCallback(() => agentParticipantRef.current?.remove(), []);
  const pin = useCallback(d => agentParticipantRef.current?.pin(d), []);
  const unpin = useCallback(d => agentParticipantRef.current?.unpin(d), []);
  const getAudioStats = useCallback(
    async () => agentParticipantRef.current?.getAudioStats(),
    []
  );
  const getVideoStats = useCallback(
    async () => agentParticipantRef.current?.getVideoStats(),
    []
  );

  const consumeWebcamStreams = useCallback(
    () => agentParticipantRef.current?.consumeWebcamStreams(),
    []
  );
  const consumeMicStreams = useCallback(
    () => agentParticipantRef.current?.consumeMicStreams(),
    []
  );
  const stopConsumingWebcamStreams = useCallback(
    () => agentParticipantRef.current?.stopConsumingWebcamStreams(),
    []
  );
  const stopConsumingMicStreams = useCallback(
    () => agentParticipantRef.current?.stopConsumingMicStreams(),
    []
  );

  const methodsRef = useRef(null);
  methodsRef.current = {
    pin,
    unpin,
    remove,
    getAudioStats,
    getVideoStats,
    consumeWebcamStreams,
    consumeMicStreams,
    stopConsumingWebcamStreams,
    stopConsumingMicStreams
  };

  return useMemo(
    () =>
      new Proxy(allState, {
        get(target, prop, receiver) {
          if (prop === 'agentParticipant') return agentParticipantRef.current;
          if (prop in PARTICIPANT_STATE_KEYS) {
            const pState = target[participantId];
            return pState ? pState[prop] : EMPTY_PARTICIPANT_STATE[prop];
          }
          const m = methodsRef.current;
          if (m && prop in m) {
            if (!agentParticipantRef.current) {
              return (...args) => {
                console.warn(
                  `useAgentParticipant: ${String(
                    prop
                  )}() called but AgentParticipant "${participantId}" not found.`
                );
              };
            }
            return m[prop];
          }
          return Reflect.get(target, prop, receiver);
        },
        has(target, prop) {
          return (
            prop === 'agentParticipant' ||
            prop in PARTICIPANT_STATE_KEYS ||
            !!(methodsRef.current && prop in methodsRef.current)
          );
        },
        ownKeys() {
          const stateKeys = Object.keys(PARTICIPANT_STATE_KEYS);
          const mKeys = Object.keys(methodsRef.current ?? {});
          return [...new Set(['agentParticipant', ...stateKeys, ...mKeys])];
        },
        getOwnPropertyDescriptor() {
          return { configurable: true, enumerable: true, writable: false };
        }
      }),
    [allState, participantId]
  );
};

export default useAgentParticipant;
